import { createRoot, events } from "@react-three/fiber";
import { useEffect, useRef } from "react";
import { EighthWallRuntime } from "@/runtime/EighthWallRuntime";
import type { PlacementPose, RuntimePhase, RuntimeSnapshot } from "@/runtime/types";
import { SceneContents } from "@/scene/SceneStage";
import { qualityPolicy, renderQualityForMode } from "@/scene/qualityPolicy";
import type { SceneStageProps } from "@/scene/types";
import { attachWebglLifecycle } from "@/scene/webglLifecycle";

interface EighthWallSceneStageProps extends Omit<SceneStageProps, "mode"> {
  runtime: EighthWallRuntime;
  onRuntimePhase: (phase: RuntimePhase) => void;
  onRuntimeError: (message: string) => void;
}

export function EighthWallSceneStage(props: EighthWallSceneStageProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const latestProps = useRef(props);
  const externalRoot = useRef<ReturnType<typeof createRoot> | null>(null);
  const placementRef = useRef<PlacementPose | null>(props.placement);

  useEffect(() => {
    latestProps.current = props;
    placementRef.current = props.placement;
    if (!externalRoot.current) return;
    externalRoot.current.render(sceneElement(props));
  });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    let disposed = false;

    const unsubscribe = props.runtime.subscribe((snapshot) => {
      if (disposed) return;
      applySnapshot(snapshot, placementRef.current, latestProps.current.onRuntimePhase, latestProps.current.onRuntimeError);
    });

    let lastPlacementRequest = 0;
    const requestPlacement = async (clientX: number, clientY: number, target: EventTarget | null) => {
      const now = performance.now();
      if (now - lastPlacementRequest < 350 || placementRef.current || (target as HTMLElement | null)?.closest("[data-ui-control]")) return;
      lastPlacementRequest = now;
      const rect = canvas.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      const pose = await props.runtime.requestPlacement({
        x: (clientX - rect.left) / rect.width,
        y: (clientY - rect.top) / rect.height,
      });
      if (disposed) return;
      if (!pose) {
        latestProps.current.onPlacementProgress(0);
        return;
      }
      const placedPose = { ...pose, scale: 0.55 };
      props.runtime.acceptPlacement(placedPose);
      latestProps.current.onPlacement(placedPose);
    };

    const onPointerUp = (event: PointerEvent) => {
      void requestPlacement(event.clientX, event.clientY, event.target);
    };
    const onTouchEnd = (event: TouchEvent) => {
      const touch = event.changedTouches[0];
      if (touch) void requestPlacement(touch.clientX, touch.clientY, event.target);
    };

    const onVisibilityChange = () => {
      if (document.hidden) void props.runtime.pause();
      else void props.runtime.resume();
    };

    // Capture the gesture before the XR engine can stop propagation.
    canvas.addEventListener("pointerup", onPointerUp, true);
    canvas.addEventListener("touchend", onTouchEnd, true);
    document.addEventListener("visibilitychange", onVisibilityChange);
    void props.runtime.start({
      canvas,
      mountScene: async (context) => {
        const root = createRoot(canvas);
        externalRoot.current = root;
        (context.camera as typeof context.camera & { manual: boolean }).manual = true;
        let detachWebglLifecycle: (() => void) | null = null;
        try {
          await root.configure({
            gl: context.renderer,
            camera: context.camera,
            frameloop: "never",
            events,
            shadows: qualityPolicy(latestProps.current.quality, true).shadows,
            onCreated: ({ gl }) => {
              gl.domElement.dataset.testid = "experience-canvas";
            },
          });
          detachWebglLifecycle = latestProps.current.onWebglContextLost
            ? attachWebglLifecycle(context.renderer.domElement, { onLost: latestProps.current.onWebglContextLost })
            : null;
          const store = root.render(sceneElement(latestProps.current));
          const r3fScene = store.getState().scene;
          context.parentScene.add(r3fScene);
          let unmounted = false;
          return {
            advance: (timestamp: number) => store.getState().advance(timestamp, true),
            unmount: () => {
              if (unmounted) return;
              unmounted = true;
              detachWebglLifecycle?.();
              detachWebglLifecycle = null;
              externalRoot.current = null;
              context.parentScene.remove(r3fScene);
              root.unmount();
            },
          };
        } catch (cause) {
          detachWebglLifecycle?.();
          detachWebglLifecycle = null;
          externalRoot.current = null;
          root.unmount();
          throw cause;
        }
      },
    }).catch((cause: unknown) => {
      if (!disposed) latestProps.current.onRuntimeError(cause instanceof Error ? cause.message : "8th Wall could not start.");
    });

    return () => {
      disposed = true;
      unsubscribe();
      canvas.removeEventListener("pointerup", onPointerUp, true);
      canvas.removeEventListener("touchend", onTouchEnd, true);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      void props.runtime.stop("unmount");
    };
  }, [props.runtime]);

  return <canvas ref={canvasRef} className="experience-canvas eighth-wall-canvas" data-testid="eighth-wall-canvas" />;
}

function sceneElement(props: EighthWallSceneStageProps) {
  return <SceneContents {...props} mode="eighth-wall" quality={renderQualityForMode(props.quality, "eighth-wall")} />;
}

function applySnapshot(
  snapshot: RuntimeSnapshot,
  placement: PlacementPose | null,
  onPhase: (phase: RuntimePhase) => void,
  onError: (message: string) => void,
) {
  if (snapshot.phase === "error") {
    onError(snapshot.error ?? "8th Wall encountered an error.");
    return;
  }
  if (placement && snapshot.phase === "searching-surface") onPhase("placed");
  else onPhase(snapshot.phase);
}
